import { useApi } from '@/api';
import { ChartTimeFilter, ExpandableKpiCard } from '@/components';
import { useChartFillDatasetsDates, useSparkColor } from '@/hooks';
import {
  CurrencyValue,
  formatNumber,
  KpiCard,
  KpiSkeleton,
  LineChart,
  useIsMobile
} from '@blockanalitica/ui';
import { SimpleChartData } from '@blockanalitica/ui/package/types/src/components/charts/SimpleChart';
import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router';

type DataResponse = {
  address: string;
  network: string;
  wallet_address: string;
  asset_symbol: string;
  asset_address: string;
  asset_name: string;
  symbol: string;
  name: string;
  principal: string;
  principal_change: string;
  assets: string;
  assets_change: string;
  assets_usd: string;
  assets_usd_change: string;
  profit: string;
  profit_change: string;
  profit_total: string;
  profit_total_change: string;
  profit_total_usd: string;
  profit_total_usd_change: string;
  profit_estimated: string;
  profit_estimated_change: string;
  profit_estimated_usd: string;
  profit_estimated_usd_change: string;
  rate: string;
  rate_change: string;
  profit_claimed_total: string;
  historic: SimpleChartData[];
  underlying_rate: string;
  asset_rate: string;
};

export default function RevenueUntilNow() {
  const color = useSparkColor();
  const isMobile = useIsMobile();
  const [chartDaysAgo, setChartDaysAgo] = useState('30');
  const { network, address, tokenAddress } = useParams();
  const [data, error] = useApi<DataResponse>({
    url: `/sparkstar/sll/tokens/${network}/${address}/${tokenAddress}/`,
    queryParams: {
      days_ago: chartDaysAgo
    }
  });

  const datasets = [
    {
      xField: 'date',
      yField: 'profit_total',
      borderColor: color
    }
  ];

  const chartData = useChartFillDatasetsDates(
    data?.historic,
    datasets,
    chartDaysAgo
  );

  const valueFormatter = useCallback((value: string | number) => {
    return <CurrencyValue value={value} variant="dynamic" />;
  }, []);

  const customConfig = useMemo(
    () => ({
      options: {
        aspectRatio: 2.5
      }
    }),
    []
  );

  if (error) {
    throw error;
  }

  if (!data) {
    return <KpiSkeleton />;
  }

  return (
    <ExpandableKpiCard
      text="Total Revenue Until Now"
      hoverBorderColor={color}
      defaultShowChart={!isMobile}
      kpiCard={
        <KpiCard
          data={{
            profit_total: data['profit_total'],
            profit_total_change: data['profit_total_change']
          }}
          field="profit_total"
          variant="nestedAsChild"
          options={{
            notation: 'compact'
          }}
        />
      }>
      {/* <Flex flexDirection="column" gap="2xs">
        <Flex flexDirection="row" gap="s" justifyContent="space-between">
          <Text>Claimed revenue:</Text>
          <CurrencyValue value={Number(data['profit_claimed_total'])} />
        </Flex>
        <Flex flexDirection="row" gap="s" justifyContent="space-between">
          <Text>Accrued revenue:</Text>
          <CurrencyValue
            value={
              Number(data['profit_total']) -
              Number(data['profit_claimed_total'])
            }
          />
        </Flex>
        <Flex flexDirection="row" gap="s" justifyContent="space-between">
          <Text>Daily revenue:</Text>
          <CurrencyValue value={data['profit']} />
        </Flex>
      </Flex>

      <Card backgroundColor="foreground">
        
      </Card> */}
      <LineChart
        data={chartData}
        datasets={datasets}
        config={customConfig}
        valueFormatter={valueFormatter}
        showValuesInTooltip={true}
        tooltip={{
          valueFormatter: (value) => {
            return formatNumber(Number(value));
          },
          labelFormatter: () => 'Total',
          displayColors: false
        }}
        timeFilter={
          <ChartTimeFilter
            activeOption={chartDaysAgo}
            onChange={setChartDaysAgo}
          />
        }
      />
    </ExpandableKpiCard>
  );
}
